// Phase 4c — dashboard scope registry + hierarchical child dispatch.
//
// Mirrors the established `aggMeta` + `compileAggPair` precedent
// (ops/transform/agg-funcs.ts): one `z.registry` holds every dashboard
// op's meta; `(dashboard …)` dispatches tiles and each tile dispatches
// its sub-forms through `dispatchDashboardChild`, the SSOT lookup +
// validate + apply step. Tiles and sub-forms are NEVER added to the
// engine's `opMeta`/`pipelineVerbs` tables — they are only reachable
// through this hierarchical dispatch, which gives context restriction
// for free (a `series` at top level is an `UnknownOpError` because it
// is not in `opMeta`; a `column` inside `(cartesian …)` is an
// `UnknownOpError` because it is not in the cartesian child table).

import { z } from 'zod';

import type { EvalContext } from '../../EvalContext.ts';
import type { Env, EvalResult } from '../../env.ts';
import { argRefFromZodPath, parseOpCall, ShapeError, UnknownOpError } from '../../errors.ts';
import { type AstInput, isOpCall, nodePos, opCallPos, type Spans } from '../../normalize.ts';
import { type NameTable, resolveOpCall, type SpecialOpMeta } from '../../op-meta.ts';
import { kwargPath } from '../../path.ts';
import { type ColumnRef, expectColumnRef } from '../../value.ts';
import type {
  BandSpec,
  CartesianSpec,
  ColumnSpec,
  MetricSpec,
  ReferenceAreaSpec,
  ReferenceLineSpec,
  SeriesSpec,
  TableSpec,
  XAxisSpec,
  YAxisSpec,
} from './value.ts';

/** Every value a dashboard op's `apply` can produce. */
export type DashboardOutput =
  | MetricSpec
  | CartesianSpec
  | TableSpec
  | SeriesSpec
  | ReferenceLineSpec
  | ReferenceAreaSpec
  | BandSpec
  | XAxisSpec
  | YAxisSpec
  | ColumnSpec;

/**
 * Context handed to a dashboard tile / sub-form `apply`. A structural
 * subset of `SpecialCtx` — the dashboard op's `apply` (a real
 * special-form, registered in `opMeta`) receives the full `SpecialCtx`
 * and passes a path-rebased projection down the hierarchy. Tiles /
 * sub-forms only ever need to recursively evaluate their deferred
 * `(col …)` kwargs (`ctx.evalNode`) and report error positions
 * (`spans` + `path`).
 */
export type DashboardCtx = {
  readonly env: Env;
  readonly spans: Spans;
  readonly path: string;
  readonly evalCtx: EvalContext;
  readonly evalNode: (node: AstInput, env: Env, path: string) => Promise<EvalResult>;
};

/**
 * The single dashboard-scope registry. Tiles and sub-forms register
 * here; `(dashboard …)` is the only op in the engine's `opMeta`.
 *
 * Dashboard ops ARE special forms and reuse the engine's
 * `SpecialOpMeta` verbatim (no bespoke meta — that would byte-for-byte
 * duplicate it). Same loose-storage contract: `data` keeps Zod's
 * `$output` marker so `dashboardMeta.get(schema)` substitutes the
 * per-schema `z.infer` (precise `data` typing in each op's `apply`
 * with no `z.infer` annotation); `ctx`/return widen so `$replace`
 * doesn't recurse into the engine's deep types. Authors annotate the
 * arrow with the dashboard context: `apply: async (data, ctx:
 * DashboardCtx): Promise<SeriesSpec> => …`. `apply` returns an
 * engine-internal spec object (NOT a `Value`); only the top-level
 * `(dashboard …)` op wraps the assembled tiles into the lone
 * `DashboardSpec` `Value`.
 */
export const dashboardMeta = z.registry<SpecialOpMeta>();

/**
 * Resolve + validate + apply one dashboard child call. SSOT for
 * "dispatch a tile from `(dashboard …)`" and "dispatch a sub-form from
 * a tile". `table` scopes which children are legal in this position
 * (context restriction); a name absent from it throws `UnknownOpError`
 * with the legal set, a shape mismatch throws `ShapeError`.
 */
export const dispatchDashboardChild = async (
  node: AstInput,
  table: NameTable,
  ctx: DashboardCtx,
  childPath: string,
): Promise<DashboardOutput> => {
  const pos = opCallPos(ctx.spans, childPath);
  if (!isOpCall(node)) {
    throw new ShapeError('expected a dashboard tile / sub-form call', pos);
  }
  const { schema, meta } = resolveOpCall(node.name, table, dashboardMeta, pos, 'dashboard op', UnknownOpError);
  const parsed = parseOpCall(schema, node);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new ShapeError(
      `invalid \`${node.name}\` call: ${issue?.message ?? 'shape mismatch'}`,
      pos,
      issue === undefined ? undefined : argRefFromZodPath(issue.path),
    );
  }
  return (await meta.apply(parsed.data as never, { ...ctx, path: childPath })) as DashboardOutput;
};

/**
 * Evaluate a deferred `(col …)` kwarg to a `ColumnRef`. SSOT for the
 * "a tile/sub-form holds a column slot" pattern — `series :x/:y`,
 * `metric :value/:delta`, `column :ref`. The raw `(col …)` OpCall is
 * evaluated through the normal engine path (`ctx.evalNode`), then
 * narrowed; a non-`ColumnRef` result raises `TypeMismatchError` via
 * `expectColumnRef`. The V-shape `ColumnRef` is stored unresolved —
 * no I/O until the renderer's per-tile resolution (4c.7).
 */
export const resolveColKwarg = async (raw: unknown, ctx: DashboardCtx, kwarg: string): Promise<ColumnRef> => {
  const path = kwargPath(ctx.path, kwarg);
  const { value } = await ctx.evalNode(raw as AstInput, ctx.env, path);
  return expectColumnRef(value, `:${kwarg} (col …)`, nodePos(ctx.spans, path));
};
